import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { Footer, BackButton } from 'signup-components/components/SidePanelLayout/SidePanelLayout';
import { Step } from 'signup-constants/Step';
import { getNextSteps } from '../../store/flow/selectors';
import { goPrevStep } from '../../store/flow/action-creators';
import { useTracker } from '../../tracking/useTracker';
import { getIsAccountCreationStep } from '../../flow-management/getIsAccountCreationStep';
import { useCanGoBack } from '../../hooks/useCanGoBack';
import { useCurrentStep } from '../../hooks/useCurrentStep';
import { useIsStepDataValid } from '../../hooks/useIsStepDataValid';
import { useView } from '../useView';
import { usePrivacyBanner } from '../../views-components/usePrivacyBanner';
import HubletLocation from '../../views-components/HubletLocation';
import CreateAccountTermsOfService from '../../views-components/CreateAccountTermsOfService';
import { useIndustry } from '../../step-industry/useIndustry';
import { useResendEmail } from '../../hooks/useResendEmail';
import { useSidePanelEnterKeyboardSubmit } from '../../hooks/useSidePanelEnterKeyboardSubmit';
import { useIsGTKMDataValidWithoutDomain } from '../../utils/gtkm/useIsFilteredGTKMDataValid';
import { OptionalPhoneNumberSkipButton } from './OptionalPhoneNumberSkipButton';
import { usePhoneNumberOptionalSkip } from '../../step-phone-number/hooks/usePhoneNumberOptionalSkip';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const SidePanelBaseViewFooter = ({
  hideSidePanelNextButton,
  stepKey,
  label
}) => {
  const Tracker = useTracker();
  const dispatch = useDispatch();
  const {
    currentStep
  } = useCurrentStep();
  const dataSeleniumAttr = `${currentStep}_button`;
  const isStepDataValid = useIsStepDataValid(currentStep);
  const {
    clickedToSeeAll,
    submitIndustry,
    predictionsFetchedGTKM
  } = useIndustry();
  const isIndustrySearch = stepKey === Step.Industry && clickedToSeeAll;
  const {
    resend
  } = useResendEmail();
  const {
    shouldSkipHublet: shouldEnableOptionalPhoneNumberNextButton
  } = usePhoneNumberOptionalSkip();
  const isGTKMDataValid = useIsGTKMDataValidWithoutDomain();
  const shouldEnableReviewStepNextButton = stepKey === Step.Review && isGTKMDataValid;
  const isNextButtonEnabled = stepKey === Step.Review ? shouldEnableReviewStepNextButton : isStepDataValid || shouldEnableOptionalPhoneNumberNextButton;
  const {
    submit
  } = useView({
    stepKey
  });
  const onSubmit = event => {
    event.preventDefault();
    if (currentStep === Step.ResendEmail) {
      resend();
    }
    if (isIndustrySearch) {
      submitIndustry();
    } else {
      submit();
    }
  };
  useSidePanelEnterKeyboardSubmit({
    isNextButtonEnabled,
    onSubmit,
    currentStep
  });
  const {
    shouldShowToSNonOptionalStep,
    shouldShowToSOptionalStep
  } = usePrivacyBanner();
  const isAccountCreationStep = useSelector(state => getIsAccountCreationStep(state, getNextSteps(state)));
  const showCreateAccountText = shouldShowToSNonOptionalStep || shouldShowToSOptionalStep || isAccountCreationStep || currentStep === Step.CmsSignupIntent;
  const shouldShowNextButton = !hideSidePanelNextButton || isIndustrySearch || predictionsFetchedGTKM || stepKey === Step.HubletSelection;
  const buttonLabel = useMemo(() => {
    if (currentStep === Step.ResendEmail) {
      return 'button.resendEmail';
    }
    if (showCreateAccountText) {
      return 'button.createAccount';
    }
    if (label && isValidI18nKey(label)) {
      return label;
    }
    const customButtonLabelI18n = `views.${currentStep}.buttonLabel`;
    if (isValidI18nKey(customButtonLabelI18n)) {
      return customButtonLabelI18n;
    }
    return 'button.next';
  }, [showCreateAccountText, label, currentStep]);
  const shouldShowToSOptionalPhonenumber = shouldShowToSOptionalStep && currentStep === Step.OptionalPhoneNumber;
  const shouldShowTermsOfService = shouldShowToSNonOptionalStep || shouldShowToSOptionalPhonenumber;
  const {
    canGoBack,
    completedStep
  } = useCanGoBack();
  const shouldShowBackButton = Boolean(canGoBack && completedStep);
  const onBackLinkClick = event => {
    event.preventDefault();
    Tracker.backButton(currentStep, completedStep);
    dispatch(goPrevStep());
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: (shouldShowBackButton || shouldShowNextButton) && /*#__PURE__*/_jsxs(Footer, {
      marginBlockStart: "500",
      children: [shouldShowBackButton && /*#__PURE__*/_jsx(BackButton, {
        onClick: onBackLinkClick,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'button.back'
        })
      }), !hideSidePanelNextButton && /*#__PURE__*/_jsxs(_Fragment, {
        children: [!shouldShowTermsOfService && showCreateAccountText && /*#__PURE__*/_jsx(View, {
          alignSelf: "center",
          children: /*#__PURE__*/_jsx(Text, {
            color: "text-primary-disabled",
            marginInlineEnd: "400",
            textAlign: "right",
            typography: "detail-200",
            children: /*#__PURE__*/_jsx(HubletLocation, {})
          })
        }), shouldShowTermsOfService && /*#__PURE__*/_jsx(View, {
          alignSelf: "center",
          children: /*#__PURE__*/_jsx(CreateAccountTermsOfService, {})
        })]
      }), /*#__PURE__*/_jsx(OptionalPhoneNumberSkipButton, {}), /*#__PURE__*/_jsx(View, {
        children: shouldShowNextButton && /*#__PURE__*/_jsx(LoadingButton, {
          "data-selenium": dataSeleniumAttr,
          disabled: !isNextButtonEnabled,
          size: "medium",
          type: "submit",
          use: "primary",
          onClick: onSubmit,
          children: /*#__PURE__*/_jsx(Flex, {
            alignItems: "center",
            justifyContent: "center",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: buttonLabel
            })
          })
        })
      })]
    })
  });
};